import { TILE_VALUES } from '@scrabble-daily/engine';

type TileSize = 'xs' | 'sm' | 'lg';

const SIZES: Record<TileSize, { tile: string; value: string; gap: string }> = {
  xs: { tile: 'h-5 w-5 rounded text-[11px]', value: 'hidden', gap: 'gap-[3px]' },
  sm: { tile: 'h-7 w-7 rounded-md text-sm', value: 'text-[7px]', gap: 'gap-1' },
  lg: {
    tile: 'h-9 w-9 rounded-md text-xl sm:h-11 sm:w-11 sm:text-2xl',
    value: 'text-[8px] sm:text-[9px]',
    gap: 'gap-1 sm:gap-1.5',
  },
};

interface LetterTilesProps {
  word: string;
  size?: TileSize;
  showValues?: boolean;
  label?: string;
  className?: string;
}

/** A word rendered as Scrabble tiles — the house wordmark style. */
export function LetterTiles({
  word,
  size = 'sm',
  showValues = true,
  label,
  className = '',
}: LetterTilesProps) {
  const s = SIZES[size];
  return (
    <span
      role="img"
      aria-label={label ?? word}
      className={`inline-flex flex-wrap justify-center ${s.gap} ${className}`}
    >
      {word.split('').map((ch, i) =>
        // A space is the gap BETWEEN words, not a tile — you don't play a
        // blank for it. Rendering one wasted a full tile's width per space,
        // which is what pushed a 24-character leaderboard name (the validated
        // maximum) off the side of the screen.
        ch === ' ' ? (
          <span key={`sp-${i}`} aria-hidden="true" className="w-1 sm:w-1.5" />
        ) : (
          <span
            key={`${ch}-${i}`}
            aria-hidden="true"
            className={`relative inline-flex select-none items-center justify-center border-b-[3px] border-tile-edge bg-tile-face font-bold text-stone-900 shadow-sm ${s.tile}`}
          >
            {ch}
            {showValues && TILE_VALUES[ch] ? (
              <span className={`absolute bottom-px right-0.5 font-medium leading-none ${s.value}`}>
                {TILE_VALUES[ch]}
              </span>
            ) : null}
          </span>
        ),
      )}
    </span>
  );
}
